import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, LogOut, Heart, Package, KeyRound, Share2, BookOpen, Award, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "The ritual failed. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30";

function trackingInfo(raw) {
  const n = (raw || "").trim();
  if (/^1Z[0-9A-Z]{16}$/i.test(n))
    return { carrier: "UPS", url: `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}` };
  if (/^9[245]\d{19,}$/.test(n) || /^\d{22}$/.test(n))
    return { carrier: "USPS", url: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}` };
  if (/^\d{12}$/.test(n) || /^\d{15}$/.test(n))
    return { carrier: "FedEx", url: `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}` };
  return { carrier: "carrier", url: `https://t.17track.net/en#nums=${encodeURIComponent(n)}` };
}

export default function Account() {
  const { user, login, register, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState([]);
  const [badges, setBadges] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [reading, setReading] = useState(null); // { url, title }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/auth/orders`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/auth/badges`, { withCredentials: true }).then((r) => setBadges(r.data.badges)).catch(() => {});
    axios.get(`${API}/auth/referrals`, { withCredentials: true }).then((r) => setReferrals(r.data)).catch(() => {});
  }, [user]);

  const shareWishlist = async () => {
    const url = `${window.location.origin}/wishlist/${user.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Want-list link copied — send it to your gift-givers.");
    } catch {
      toast.info(url);
    }
  };

  const shareReferral = async () => {
    if (!referrals) return;
    const url = `${window.location.origin}/r/${referrals.code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Blood-oath link copied — recruit a fiend, earn 10% off.");
    } catch {
      toast.info(url);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (tab === "login") await login(email, password);
      else await register(name, email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: curPw, new_password: newPw },
        { withCredentials: true }
      );
      toast.success("Password reborn. The crypt recognizes your new key.");
      setCurPw("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  const wished = user ? products.filter((p) => (user.wishlist || []).includes(p.id)) : [];

  return (
    <div data-testid="account-page" className="min-h-screen bg-void newsprint-grain">
      <header className="fixed top-0 inset-x-0 z-40 border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="account-brand" className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <div className="flex items-center gap-4">
            <a
              href="/"
              data-testid="account-back-link"
              className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
            >
              <ArrowLeft size={14} /> Back to the Crypt
            </a>
            <button
              data-testid="nav-cart-trigger"
              onClick={() => setOpen(true)}
              className="relative p-2 text-paper transition-colors duration-150 hover:text-bloodglow"
              aria-label="Open cart"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-crimson text-white font-impact text-[10px] w-5 h-5 flex items-center justify-center rounded-full border border-black">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="pt-16">
        {user === undefined ? (
          <p className="font-type text-paper/50 text-center py-32">Summoning your crypt...</p>
        ) : !user ? (
          <div className="mx-auto max-w-md px-4 sm:px-6 py-20">
            <p className="font-marker text-paper text-base sm:text-lg mb-3 -rotate-1 inline-block">the crypt has a guest list</p>
            <h1 className="font-display text-3xl sm:text-4xl uppercase text-paper mb-8">
              YOUR <span className="text-crimson">CRYPT</span>
            </h1>
            <div className="border-2 border-blooddeep bg-pulp p-6 sm:p-8">
              {tab === "forgot" ? (
                <form onSubmit={forgotSubmit} className="space-y-3">
                  <p className="font-impact uppercase tracking-wider text-sm text-paper mb-1 flex items-center gap-2">
                    <KeyRound size={16} className="text-crimson" /> Resurrect Your Password
                  </p>
                  {forgotSent ? (
                    <p data-testid="forgot-success-message" className="font-type text-sm text-paper/70">
                      If that email walks these halls, a reset link is crawling toward its inbox. The link dies in one hour.
                    </p>
                  ) : (
                    <>
                      <input
                        data-testid="forgot-email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email"
                        className={inputCls}
                      />
                      {error && (
                        <p data-testid="auth-error-message" className="font-type text-xs text-bloodglow">
                          {error}
                        </p>
                      )}
                      <button
                        data-testid="forgot-submit-button"
                        type="submit"
                        disabled={busy}
                        className="w-full font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-6 py-3.5 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 disabled:opacity-50"
                      >
                        {busy ? "Summoning..." : "Send Reset Link"}
                      </button>
                    </>
                  )}
                  <button
                    data-testid="forgot-back-link"
                    type="button"
                    onClick={() => {
                      setTab("login");
                      setError("");
                      setForgotSent(false);
                    }}
                    className="w-full text-center font-courier text-[11px] uppercase tracking-widest text-paper/45 hover:text-crimson transition-colors duration-150 pt-1"
                  >
                    Back to login
                  </button>
                </form>
              ) : (
              <>
              <div className="flex gap-2 mb-6">
                <button
                  data-testid="auth-tab-login"
                  onClick={() => {
                    setTab("login");
                    setError("");
                  }}
                  className={`flex-1 font-impact uppercase tracking-wider text-xs px-4 py-2.5 border-2 border-black transition-colors duration-150 ${
                    tab === "login" ? "bg-crimson text-white" : "bg-charcoal text-paper/60 hover:text-paper"
                  }`}
                >
                  Enter
                </button>
                <button
                  data-testid="auth-tab-register"
                  onClick={() => {
                    setTab("register");
                    setError("");
                  }}
                  className={`flex-1 font-impact uppercase tracking-wider text-xs px-4 py-2.5 border-2 border-black transition-colors duration-150 ${
                    tab === "register" ? "bg-crimson text-white" : "bg-charcoal text-paper/60 hover:text-paper"
                  }`}
                >
                  Join the Coven
                </button>
              </div>
              <form onSubmit={submit} className="space-y-3">
                {tab === "register" && (
                  <input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name for the ledger"
                    className={inputCls}
                  />
                )}
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className={inputCls}
                />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={tab === "register" ? "Password (8+ characters)" : "Password"}
                  className={inputCls}
                />
                {error && (
                  <p data-testid="auth-error-message" className="font-type text-xs text-bloodglow">
                    {error}
                  </p>
                )}
                <button
                  data-testid="auth-submit-button"
                  type="submit"
                  disabled={busy}
                  className="w-full font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-6 py-3.5 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 disabled:opacity-50"
                >
                  {busy ? "Knocking..." : tab === "login" ? "Enter the Crypt" : "Join the Coven"}
                </button>
                {tab === "login" && (
                  <button
                    data-testid="forgot-password-link"
                    type="button"
                    onClick={() => {
                      setTab("forgot");
                      setError("");
                      setForgotSent(false);
                    }}
                    className="w-full text-center font-courier text-[11px] uppercase tracking-widest text-paper/45 hover:text-crimson transition-colors duration-150 pt-1"
                  >
                    Forgot your password?
                  </button>
                )}
              </form>
              <p className="font-courier text-[10px] text-paper/35 text-center uppercase tracking-widest mt-4">
                Google &amp; Facebook entry coming soon
              </p>
              </>
              )}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-5xl px-4 sm:px-6 py-16">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div>
                <p className="font-marker text-paper text-base sm:text-lg mb-3 -rotate-1 inline-block">your crypt awaits</p>
                <h1 data-testid="account-greeting" className="font-display text-3xl sm:text-4xl uppercase text-paper">
                  WELCOME BACK, <span className="text-crimson">{user.name}</span>
                </h1>
                <p className="font-courier text-xs text-paper/50 mt-2">{user.email}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={logout}
                className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 border-2 border-paper/30 px-4 py-2.5 transition-colors duration-150 hover:border-crimson hover:text-crimson"
              >
                <LogOut size={14} /> Leave the Crypt
              </button>
            </div>

            <h2 className="font-impact uppercase tracking-widest text-lg text-paper mb-5 flex items-center gap-2">
              <Heart size={18} className="text-crimson fill-crimson" /> Your Want-List
              {user && (user.wishlist || []).length > 0 && (
                <button
                  data-testid="share-wishlist-button"
                  onClick={shareWishlist}
                  className="ml-auto flex items-center gap-1.5 font-impact uppercase tracking-wider text-[11px] text-cyanx border border-cyanx/40 px-3 py-1.5 transition-colors duration-150 hover:bg-cyanx/10"
                >
                  <Share2 size={12} /> Share It
                </button>
              )}
            </h2>
            {wished.length === 0 ? (
              <p data-testid="account-wishlist-empty" className="font-type text-sm text-paper/50 mb-14">
                No hearts given yet. Tap the heart on any shop item to save it here.
              </p>
            ) : (
              <div data-testid="account-wishlist-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 mb-14">
                {wished.map((p) => (
                  <a key={p.id} href="/shop" data-testid={`account-wishlist-item-${p.id}`} className="group border-2 border-border bg-pulp transition-colors duration-200 hover:border-crimson">
                    <div className="spotlight-frame aspect-[2/3] border-0">
                      <img src={p.cover} alt={`${p.title} cover`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    </div>
                    <div className="p-3">
                      <p className="font-impact uppercase tracking-wide text-sm text-paper group-hover:text-crimson transition-colors duration-150">{p.title}</p>
                      <p className="font-marker text-bloodglow text-sm mt-1">${p.price.toFixed(2)}</p>
                    </div>
                  </a>
                ))}
              </div>
            )}

            <h2 className="font-impact uppercase tracking-widest text-lg text-paper mb-5 flex items-center gap-2">
              <Award size={18} className="text-crimson" /> Collection Badges
            </h2>
            <div data-testid="account-badges" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
              {(badges || []).map((b) => (
                <div
                  key={b.series}
                  data-testid={`badge-${b.series.replace(/\s+/g, "-")}`}
                  className={`border-2 p-4 ${b.complete ? "border-yell bg-yell/5" : "border-border bg-pulp"}`}
                >
                  <p className="font-impact uppercase tracking-wide text-sm text-paper">
                    {b.series}
                    {b.complete && (
                      <span className="ml-2 font-marker text-[10px] bg-yell text-black px-2 py-0.5 -rotate-2 inline-block ink-shadow-sm">
                        FULL CLIP
                      </span>
                    )}
                  </p>
                  <p className="font-courier text-[11px] text-paper/50 mt-1.5">
                    {b.owned} of {b.total} issues in your crypt
                  </p>
                  <div className="mt-2 h-1.5 bg-void border border-blooddeep/40">
                    <div
                      className={`h-full ${b.complete ? "bg-yell" : "bg-crimson"}`}
                      style={{ width: `${b.total ? Math.round((b.owned / b.total) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
              {badges && badges.length === 0 && (
                <p className="font-type text-sm text-paper/50">No series to collect yet.</p>
              )}
            </div>

            <h2 className="font-impact uppercase tracking-widest text-lg text-paper mb-5 flex items-center gap-2">
              <Share2 size={18} className="text-crimson" /> Blood-Oath Referrals
            </h2>
            <div data-testid="account-referrals" className="border border-blooddeep/40 bg-pulp p-5 mb-14">
              <p className="font-type text-sm text-paper/60 mb-4 leading-relaxed">
                Swear a fiend in with your link — their first purchase bleeds 10% lighter, and every recruit who pays
                earns you a personal 10% code by email.
              </p>
              {referrals && (
                <>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <code data-testid="referral-code" className="font-courier text-sm text-yell bg-void border border-yell/40 px-3 py-2 select-all">
                      {typeof window !== "undefined" ? `${window.location.origin}/r/${referrals.code}` : referrals.code}
                    </code>
                    <button
                      data-testid="share-referral-button"
                      onClick={shareReferral}
                      className="flex items-center gap-1.5 font-impact uppercase tracking-wider text-[11px] bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
                    >
                      <Share2 size={12} /> Copy Oath Link
                    </button>
                  </div>
                  <p data-testid="referral-stats" className="font-courier text-[11px] uppercase tracking-widest text-paper/50">
                    {referrals.recruits} recruit{referrals.recruits === 1 ? "" : "s"} bled
                    {referrals.recruit_names.length > 0 && ` — ${referrals.recruit_names.join(", ")}`}
                  </p>
                  {referrals.reward_codes.length > 0 && (
                    <div className="mt-3">
                      <p className="font-courier text-[10px] uppercase tracking-widest text-paper/40 mb-1.5">Your cuts (also emailed):</p>
                      <div className="flex flex-wrap gap-2">
                        {referrals.reward_codes.map((c) => (
                          <code key={c} data-testid={`reward-code-${c}`} className="font-courier text-xs text-bloodglow bg-void border border-blooddeep/50 px-2.5 py-1">
                            {c}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <h2 className="font-impact uppercase tracking-widest text-lg text-paper mb-5 flex items-center gap-2">
              <Package size={18} className="text-crimson" /> Your Orders
            </h2>
            {orders === null ? (
              <p className="font-type text-sm text-paper/50">Digging up the ledger...</p>
            ) : orders.length === 0 ? (
              <p data-testid="account-orders-empty" className="font-type text-sm text-paper/50">
                No orders haunt this account yet. Orders placed with this email will appear here.
              </p>
            ) : (
              <ul data-testid="account-orders-list" className="space-y-4">
                {orders.map((o) => (
                  <li key={o.id} data-testid={`account-order-${o.id}`} className="border-2 border-border bg-pulp p-4 sm:p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <span className="font-courier text-xs text-paper/50">
                        {new Date(o.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                      </span>
                      <span
                        className={`font-courier text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                          o.status === "shipped" ? "text-paper/60 border-paper/30" : "text-bloodglow border-blooddeep/50"
                        }`}
                      >
                        {o.status === "shipped" ? "Shipped" : "In the Crypt"}
                      </span>
                    </div>
                    <ul className="space-y-1 mb-3">
                      {(o.items || []).map((i, idx) => (
                        <li key={idx} className="font-type text-sm text-paper/80">
                          {i.title} <span className="text-paper/40">&times; {i.qty}</span>
                        </li>
                      ))}
                    </ul>
                    {(o.downloads || []).length > 0 && (
                      <div className="mb-3 border border-cyanx/25 bg-void/50 p-3">
                        <p className="font-courier text-[10px] uppercase tracking-widest text-cyanx mb-2">
                          Digital loot — re-download anytime
                        </p>
                        <ul className="space-y-1.5">
                          {o.downloads.map((d) => (
                            <li key={d.product_id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <a
                                data-testid={`account-download-${o.id}-${d.product_id}`}
                                href={d.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-type text-sm text-paper hover:text-crimson underline decoration-blooddeep underline-offset-4 transition-colors duration-150"
                              >
                                {d.title} ↓
                              </a>
                              <button
                                data-testid={`account-read-${o.id}-${d.product_id}`}
                                onClick={() => setReading({ url: d.url.replace("/api/download/", "/api/read/"), title: d.title })}
                                className="inline-flex items-center gap-1 font-courier text-[10px] uppercase tracking-widest text-cyanx border border-cyanx/40 px-2 py-0.5 transition-colors duration-150 hover:bg-cyanx/10"
                              >
                                <BookOpen size={11} /> Read
                              </button>
                              <span className="font-courier text-[10px] text-paper/40">
                                {d.downloads_left} of 5 grabs left
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-marker text-bone text-lg">${(o.total || 0).toFixed(2)}</span>
                      {o.tracking && (
                        <a
                          data-testid={`account-tracking-${o.id}`}
                          href={trackingInfo(o.tracking).url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-courier text-xs text-paper/60 hover:text-crimson transition-colors duration-150 underline decoration-blooddeep underline-offset-4"
                        >
                          Track via {trackingInfo(o.tracking).carrier}: <span className="text-bloodglow">{o.tracking}</span> ↗
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h2 className="font-impact uppercase tracking-widest text-lg text-paper mb-5 mt-14 flex items-center gap-2">
              <KeyRound size={18} className="text-crimson" /> Change Password
            </h2>
            <form onSubmit={changePw} className="border-2 border-border bg-pulp p-5 sm:p-6 space-y-3 max-w-md">
              <input
                data-testid="change-current-password-input"
                type="password"
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                placeholder="Current password"
                className={inputCls}
              />
              <input
                data-testid="change-new-password-input"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="New password (8+ characters)"
                className={inputCls}
              />
              <button
                data-testid="change-password-submit-button"
                type="submit"
                disabled={pwBusy}
                className="font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-5 py-2.5 ink-shadow-sm transition-all duration-150 hover:bg-crimsonhot disabled:opacity-50"
              >
                {pwBusy ? "Working..." : "Change Password"}
              </button>
            </form>
          </div>
        )}
        {reading && (
          <div data-testid="pdf-reader-overlay" className="fixed inset-0 z-[70] bg-void/95 backdrop-blur-md flex flex-col p-4">
            <div className="flex items-center justify-between max-w-6xl w-full mx-auto mb-3">
              <p className="font-marker text-bloodglow text-sm -rotate-1">reading: {reading.title}</p>
              <button
                data-testid="pdf-reader-close"
                onClick={() => setReading(null)}
                className="text-paper/60 hover:text-crimson transition-colors"
                aria-label="Close reader"
              >
                <X size={26} />
              </button>
            </div>
            <iframe
              src={reading.url}
              title={reading.title}
              className="flex-1 w-full max-w-6xl mx-auto border-2 border-blooddeep bg-charcoal"
            />
          </div>
        )}
      </div>
    </div>
  );
}
